import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
  getCommonNumberFilterParams,
} from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateFormatter, DateTimeFormatter } from '../../shared/controls/formatters';
import { currencyFormatterByField } from '../../shared/constants';

const OptionButton = ({
  handleClickOpenEditForm,
  paramsValue,
  handleOpenViewForm,
  rowData,
  handleClickDelete,
  hasDeleteOpportunityPermission,
  hasUpdateOpportunityPermission,
  hasInfoOpportunityPermission,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    handleClickDelete(paramsValue);
    setShowConfirmation(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="action-menu">
      {hasUpdateOpportunityPermission && (
        <img
          src="/assets/images/edit.png"
          className="action-img1"
          onClick={() => handleClickOpenEditForm(paramsValue)}
          alt="Edit Opportunity"
          title="Edit Opportunity"
        ></img>
      )}

      {hasInfoOpportunityPermission && (
        <img
          src="/assets/images/view.png"
          className="action-img2"
          onClick={() => handleOpenViewForm(paramsValue)}
          alt="View Opportunity"
          title="View Opportunity"
        ></img>
      )}

      {hasDeleteOpportunityPermission && (
        <img
          src="/assets/images/trash.png"
          className="action-img2"
          onClick={handleDeleteClick}
          alt="Delete Opportunity"
          title="Delete Opportunity"
        ></img>
      )}
      <DeleteConfirmationModal
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        handleConfirm={handleConfirmDelete}
        rowData={rowData}
      />
    </div>
  );
};

const useDealColumnDefs = ({
  hasUpdateOpportunityPermission,
  hasInfoOpportunityPermission,
  hasDeleteOpportunityPermission,
  tagsList,
  leadSourceList,
  owner,
  gridData,
  open,
  stageDropdown,
  forecastCategoryDropdown,
  industryList,
  handleClickDelete,
  projectTypeDropdown,
}) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const commonNumberFilterParams = useMemo(() => getCommonNumberFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);
  const handleClickOpenEditForm = (paramsValue) => {
    navigate('/opportunity/editOpportunity', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate(
      `/opportunity/viewOpportunity?id=${paramsValue.id}&pageNo=${gridData?.inputPayload?.pageNumber}`,
      {
        state: paramsValue,
      }
    );
  };
  const actionsColumn = useMemo(() => {
    if (
      !(
        hasUpdateOpportunityPermission ||
        hasInfoOpportunityPermission ||
        hasDeleteOpportunityPermission
      )
    )
      return [];

    return [
      {
        headerName: 'Actions',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        checkboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        cellRenderer: (params) => {
          if (!params?.node || params.node.group) return null;
          const rowData = { ...params.data, rowIndex: params.rowIndex };
          return (
            <OptionButton
              rowData={rowData}
              hasInfoOpportunityPermission={hasInfoOpportunityPermission}
              hasDeleteOpportunityPermission={hasDeleteOpportunityPermission}
              hasUpdateOpportunityPermission={hasUpdateOpportunityPermission}
              handleClickOpenEditForm={() => handleClickOpenEditForm(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
            />
          );
        },
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
    ];
  }, [
    hasUpdateOpportunityPermission,
    hasInfoOpportunityPermission,
    hasDeleteOpportunityPermission,
    handleClickOpenEditForm,
    handleOpenViewForm,
    handleClickDelete,
  ]);

  const columnDefs = useMemo(() => {
    if (
      !tagsList?.length ||
      !stageDropdown?.length ||
      !forecastCategoryDropdown?.length ||
      !industryList?.length ||
      !leadSourceList?.length ||
      !owner?.length
    )
      return [];

    return [
      ...actionsColumn,
      {
        headerName: 'Opportunity Name',
        field: 'name',
        type: 'text',
        pinned: isMobile() ? null : 'left',
        minWidth: 180,
        cellRenderer: (params) => {
          if (!params.value) return null;
          return (
            <Link
              to={`/opportunity/viewOpportunity?${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
              state={params.data}
              style={{ fontWeight: '600' }}
            >
              {params.value}
            </Link>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'account',
        type: 'text',
        pinned: isMobile() ? null : 'left',
        minWidth: 180,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Opportunity Owner',
        field: 'owner',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Project Type',
        field: 'projectType',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(projectTypeDropdown?.map((item) => item.displayName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Contact Name',
        field: 'contact',
        type: 'dropdown',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Amount',
        field: 'amount',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        valueFormatter: currencyFormatterByField('amount'),
        // hide: true,
      },
      {
        headerName: 'Stage',
        field: 'stage',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(stageDropdown?.map((item) => item.displayName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Probability',
        field: 'probability',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        valueFormatter: (params) => (params.value != null ? `${params.value}%` : ''),
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Closing Date',
        field: 'closingDate',
        type: 'date',
        valueFormatter: DateFormatter,
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams,
        // hide: true,
      },
      {
        headerName: 'Expected Revenue',
        field: 'expectedRevenue',
        type: 'dropdown',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        valueFormatter: currencyFormatterByField('expectedRevenue'),
        // hide: true,
      },
      {
        headerName: 'Campaign Source',
        field: 'campaignSource',
        type: 'dropdown',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Lead Source',
        field: 'leadSource',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(leadSourceList?.map((item) => item.displayName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Reach Out For Marketing',
        field: 'reachOutForMarketing',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Industry',
        field: 'industry',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(industryList?.map((item) => item.displayName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Business Type',
        field: 'businessType',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Source Name',
        field: 'sourceName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Other Source Name',
        field: 'otherSourceName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Line Of Business',
        field: 'lineOfBusiness',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Created Date Time (UTC)',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
        // hide: true
      },
      {
        headerName: 'Updated Date Time (UTC)',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
        // hide: true
      },
      {
        headerName: 'Tags',
        field: 'tagIds',
        valueGetter: (params) => params?.data?.tags,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(tagsList?.map((item) => item?.name[0])),
        // hide: true
      },
      {
        headerName: 'Next Step',
        field: 'nextStep',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Industry',
        field: 'industry',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(forecastCategoryDropdown?.map((item) => item.displayName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Description',
        field: 'description',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
      {
        headerName: 'Technology Stack',
        field: 'technologyStack',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
      },
    ];
  }, [
    hasInfoOpportunityPermission,
    leadSourceList,
    owner,
    tagsList,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    filterParams,
  ]);

  return columnDefs;
};

export default useDealColumnDefs;
